import { NavigateFunction } from "react-router-dom";
import { Persistor } from "redux-persist";
import { Store } from "@reduxjs/toolkit";
import { QueryClient } from "react-query";
import { sessionModalTracker } from "../Utils/sessionModalTracker";

/**
 * Navigation function type - can be React Router's navigate or a custom function
 */
type NavigationFunction = NavigateFunction | ((path: string) => void);

/**
 * Options for performing a complete logout cleanup
 */
interface LogoutCleanupOptions {
  navigate: NavigationFunction;
  store?: Store;
  resetAuthAction?: () => { type: string };
  persistor?: Persistor;
  /**
   * React Query client for clearing cached data (optional)
   */
  queryClient?: QueryClient;
  /**
   * Delay in milliseconds before navigation (useful for showing toasts)
   * @default 0
   */
  delayBeforeNavigation?: number;
}

/**
 * Performs complete logout cleanup including Redux state, storage, and navigation.
 * This is the single source of truth for logout cleanup logic across the application.
 *
 * Used by:
 * - Manual logout (NavBar components)
 * - Session expiry (useJwtExpiry hook)
 * - Session invalidation (axiosSessionInterceptor)
 *
 * @param options - Configuration for logout cleanup
 * @param options.navigate - React Router navigate function
 * @param options.store - Redux store (optional, for state reset)
 * @param options.resetAuthAction - Redux action to reset auth state (optional)
 * @param options.persistor - Redux Persist persistor (optional, for purging)
 *
 * @example
 * // Simple usage (storage cleanup + navigation only)
 * performLogoutCleanup({ navigate });
 *
 * @example
 * // Complete usage (with Redux reset)
 * performLogoutCleanup({
 *   navigate,
 *   store,
 *   resetAuthAction: resetAuthState,
 *   persistor
 * });
 */
export const performLogoutCleanup = async ({
  navigate,
  store,
  resetAuthAction,
  persistor,
  queryClient,
  delayBeforeNavigation = 0,
}: LogoutCleanupOptions): Promise<void> => {
  // Reset Redux state if provided
  if (store && resetAuthAction) {
    store.dispatch(resetAuthAction());
  }

  // Purge Redux Persist storage to prevent rehydration
  if (persistor) {
    await persistor.purge();
  }

  // Clear React Query cache if provided
  if (queryClient) {
    queryClient.clear();
  }

  // Clear all sessionStorage (all keys are auth/session-related)
  sessionStorage.clear();

  // Clear auth-related localStorage keys (preserve user preferences like lastActiveTab)
  const keysToRemove = ["userType", "onboardingModalSeen"];
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Clear dynamic user-specific keys
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("onboarding_completed_shown_")) {
      localStorage.removeItem(key);
    }
  });

  // Check navigation context before clearing modals
  const isOnLoginPage = window.location.pathname.includes("/login");
  const hasActiveSessionModal = sessionModalTracker.hasActiveModals();

  // Clear tracked modals
  sessionModalTracker.clearAll();

  // Apply delay if specified (e.g., to show toast notifications)
  if (delayBeforeNavigation > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayBeforeNavigation));
  }

  // Navigate based on context
  // If on login page with active session modal, reload to clear modal state
  if (isOnLoginPage && hasActiveSessionModal) {
    window.location.reload();
  } else {
    navigate("/login");
  }
};

